import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, RefreshCw, FileText, MapPin, TrendingUp, Headphones, MessageCircle, Phone, ArrowRight, Sparkles, CheckCircle2, Clock, UserCheck, Scale, Landmark, BadgeCheck, ShieldCheck, Award, Home as HomeIcon, Users } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { getWhatsAppLink, openWhatsApp, getPhoneLink, PHONE_NUMBER_DISPLAY } from '../constants/contact';
import { getServiceDetails } from '../constants/serviceDetails';
import { VastuSection } from './VastuSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Search: Search,
  RefreshCw: RefreshCw,
  FileText: FileText,
  MapPin: MapPin,
  TrendingUp: TrendingUp,
  Headphones: Headphones
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setInquiryForm({
      name: '',
      email: '',
      phone: '',
      message: `I'm interested in ${service.title}. Please contact me.`
    });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.phone) {
      alert('⚠️ Please fill in Name, Email, and Phone number');
      return;
    }
    
    setIsSubmitting(true);

    try {
      console.log('Submitting inquiry:', {
        name: inquiryForm.name,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        property_type: selectedService.title,
        message: inquiryForm.message
      });

      const response = await axios.post(`${API}/contact`, {
        name: inquiryForm.name,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        property_type: selectedService.title,
        message: inquiryForm.message
      });
      
      console.log('Inquiry response:', response);
      alert('✅ Your inquiry has been sent! Our team will contact you shortly.');
      setSelectedService(null);
      setInquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      console.error('Error details:', error.response?.data);
      alert('❌ Failed to send inquiry. Please try again or call us directly at +91 8597310624');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getServiceWhatsAppLink = () => {
    let message = `Hi, I'm interested in ${selectedService?.title || 'your services'}.`;
    
    if (inquiryForm.name) message += `\n\nName: ${inquiryForm.name}`;
    if (inquiryForm.email) message += `\nEmail: ${inquiryForm.email}`;
    if (inquiryForm.phone) message += `\nPhone: ${inquiryForm.phone}`;
    if (inquiryForm.message) message += `\n\nMessage: ${inquiryForm.message}`;
    
    return getWhatsAppLink(message);
  };
  return (
    <section id="services" className="am-reveal py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Our Services
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Comprehensive Real Estate
            <span className="text-emerald-700"> Solutions</span>
          </h2>
          <p className="text-lg text-slate-600">
            From consultation to post-sale support, we provide end-to-end services 
            to make your land investment journey seamless and successful.
          </p>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div 
                key={service.id}
                onClick={() => handleServiceClick(service)}
                className="am-service-card group relative bg-white border border-slate-200 rounded-2xl p-8 cursor-pointer overflow-hidden"
              >
                {/* Icon halo */}
                <div className="relative w-16 h-16 mb-6">
                  <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-50 to-amber-50 am-service-halo"></span>
                  <span className="absolute inset-0 rounded-xl flex items-center justify-center">
                    <IconComponent className="text-emerald-700 am-service-icon" size={32} />
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-5">
                  {service.description}
                </p>

                {/* Motion-graphic CTA */}
                <button
                  type="button"
                  className="btn-am-primary w-full py-3 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleServiceClick(service);
                  }}
                  data-testid={`service-cta-${service.id}`}
                >
                  <span>Get This Service</span>
                  <ArrowRight size={14} className="am-service-arrow" />
                </button>

                {/* Progress bar accent */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-emerald-600 via-amber-400 to-emerald-600 group-hover:w-full transition-all duration-500 rounded-b-2xl am-service-shimmer"></div>
              </div>
            );
          })}
          </div>

          {/* Trust bar — verified experts + live counters + testimonials */}
          <TrustBar />

            {/* Service Inquiry Dialog */}
            <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                {selectedService && (() => {
                  const details = getServiceDetails(selectedService.id);
                  return (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-2xl">{selectedService.title}</DialogTitle>
                      {details?.tagline ? (
                        <DialogDescription className="text-sm text-emerald-700 font-medium">
                          {details.tagline}
                        </DialogDescription>
                      ) : (
                        <DialogDescription className="text-sm text-slate-600">
                          Tell us what you need and our team will reach out within one business day.
                        </DialogDescription>
                      )}
                    </DialogHeader>

                    {details && (
                      <div className="mt-4 rounded-xl bg-gradient-to-br from-emerald-50 to-amber-50/40 border border-emerald-100 p-4 space-y-3" data-testid="service-details-block">
                        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide">What AcreMitra does for you</h4>
                        <ul className="space-y-2.5">
                          {details.steps.map((step, i) => (
                            <li key={i} className="flex gap-2.5">
                              <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{step.title}</div>
                                <div className="text-xs text-slate-600 leading-relaxed">{step.desc}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <div className="pt-2 border-t border-emerald-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1.5 text-slate-700">
                            <Clock size={14} className="text-emerald-600" />
                            <span><span className="font-semibold">Timeline:</span> {details.timeline}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-700">
                            <UserCheck size={14} className="text-emerald-600" />
                            <span><span className="font-semibold">Owned by:</span> {details.owner}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                          placeholder="Enter your name"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                          placeholder="your.email@example.com"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={inquiryForm.phone}
                          onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message">Message</Label>
                        <textarea
                          id="message"
                          value={inquiryForm.message}
                          onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                          rows={3}
                          className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-am-primary w-full py-3 text-sm"
                          data-testid="service-send-inquiry-btn"
                        >
                          <span>{isSubmitting ? 'Sending...' : 'Send Inquiry'}</span>
                        </button>

                        <a
                          href={getPhoneLink()}
                          className="btn-am-secondary w-full py-3 text-sm"
                          data-testid="service-call-btn"
                        >
                          <Phone size={16} />
                          <span>Call</span>
                        </a>

                        <button
                          type="button"
                          onClick={() => {
                            let message = `Hi, I'm interested in ${selectedService?.title || 'your services'}.`;
                            if (inquiryForm.name) message += `\n\nName: ${inquiryForm.name}`;
                            if (inquiryForm.email) message += `\nEmail: ${inquiryForm.email}`;
                            if (inquiryForm.phone) message += `\nPhone: ${inquiryForm.phone}`;
                            if (inquiryForm.message) message += `\n\nMessage: ${inquiryForm.message}`;
                            openWhatsApp(message);
                          }}
                          className="btn-am-whatsapp w-full py-3 text-sm"
                          data-testid="service-whatsapp-btn"
                        >
                          <MessageCircle size={16} />
                          <span>WhatsApp</span>
                        </button>
                      </div>
                      
                      <p className="text-xs text-center text-slate-500">
                        Choose to submit inquiry or chat directly on WhatsApp
                      </p>
                    </form>
                  </>
                  );
                })()}
              </DialogContent>
            </Dialog>
          </>
        )}
        <VastuSection />
      </div>
    </section>
  );
};


// -----------------------------------------------------------------------------
// TrustBar — verified-expert counters + short testimonials with motion graphics
// -----------------------------------------------------------------------------

const useCounter = (target, durationMs = 1600) => {
  const [value, setValue] = useState(0);
  const [ref, setRef] = useState(null);
  useEffect(() => {
    if (!ref) return undefined;
    let raf = 0;
    let startTs = 0;
    let started = false;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const t = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          raf = requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.25 });
    io.observe(ref);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [ref, target, durationMs]);
  return [value, setRef];
};

const CounterCard = ({ icon: Icon, target, suffix, label, subLabel, accent }) => {
  const [value, setRef] = useCounter(target);
  return (
    <div ref={setRef} className="relative rounded-2xl bg-white border border-slate-200 p-6 overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`trust-counter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full ${accent || 'bg-emerald-100'} blur-2xl opacity-40 group-hover:opacity-70 transition-opacity`} />
      <div className={`relative inline-flex w-12 h-12 rounded-xl items-center justify-center mb-3 ${accent || 'bg-emerald-50 text-emerald-700'}`}>
        <Icon size={22} />
      </div>
      <div className="relative">
        <div className="text-4xl font-black text-slate-900 tracking-tight">
          {value.toLocaleString('en-IN')}{suffix}
        </div>
        <div className="text-sm font-bold text-slate-800 mt-1">{label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{subLabel}</div>
      </div>
    </div>
  );
};

const EXPERTS = [
  {
    name: 'Adv. Anirban Chatterjee',
    role: 'Senior Property Lawyer · Kolkata HC',
    quote: 'Every plot Acre Mitra lists goes through a 12-point title chain, mutation and encumbrance verification. I have personally cleared 480+ transactions with them.',
    credential: 'BCI Reg · WB/2847/2011 · 14 yrs',
    avatar: 'https://images.unsplash.com/photo-1618077360395-f3068be8e001?w=240&h=240&fit=crop&crop=faces',
    tag: 'Verified',
  },
  {
    name: 'CA Ritwik Bose',
    role: 'Property Tax & Registration Consultant',
    quote: 'Acre Mitra\u2019s stamp-duty desk saved my clients over \u20B917 lakhs last year by structuring registrations correctly and choosing the right sub-registrar office.',
    credential: 'ICAI \u00B7 M.No. 306124 \u00B7 9 yrs',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=240&h=240&fit=crop&crop=faces',
    tag: 'Verified',
  },
  {
    name: 'Ar. Debolina Ghosh',
    role: 'Registered Architect · COA India',
    quote: 'Their AI blueprint tool is genuinely useful \u2014 it snaps to state building bye-laws, setbacks, FAR and open-space rules so my final drawings only need minor tweaks.',
    credential: 'COA \u00B7 CA/2019/103482 \u00B7 7 yrs',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=240&h=240&fit=crop&crop=faces',
    tag: 'Verified',
  },
];

const REVIEWS = [
  {
    name: 'Soumen Dey',
    location: 'Salt Lake, Kolkata',
    stars: 5,
    text: 'Bought a 3-katha plot in Kalyani through Acre Mitra. Their team handled mutation, boundary verification and even the HT wire check on the map. Zero hidden charges. Fully transparent.',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&h=200&fit=crop&crop=faces',
    when: '2 months ago',
    verified: true,
  },
  {
    name: 'Priyanka Bhattacharya',
    location: 'New Town, Kolkata',
    stars: 5,
    text: 'I was worried about buying agricultural land as an NRI. Acre Mitra\u2019s advocate panel walked me through every clause and got my Power of Attorney registered without me flying down. Amazing service.',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces',
    when: '3 weeks ago',
    verified: true,
  },
  {
    name: 'Rajarshi Sen',
    location: 'Behala, Kolkata',
    stars: 5,
    text: 'Used their Land-Fill estimator + surveyor visit for our 500 sqft plot in Sonarpur. Estimate matched the final bill within 3%. Punctual crew, invoice arrived on WhatsApp same evening.',
    avatar: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=200&h=200&fit=crop&crop=faces',
    when: '1 month ago',
    verified: true,
  },
  {
    name: 'Anindita Roy',
    location: 'Howrah',
    stars: 5,
    text: 'The AI Architect blueprint gave us three floor-plan options in minutes. Our contractor said the drawings were RERA-compliant and needed no rework. Saved us at least \u20B990,000 in architect fees.',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=faces',
    when: '2 weeks ago',
    verified: true,
  },
];

const TrustBar = () => {
  const [joinOpen, setJoinOpen] = useState(false);
  return (
  <section className="mt-16 mb-4" data-testid="services-trust-bar">
    {/* Section heading */}
    <div className="text-center max-w-2xl mx-auto mb-8">
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-black uppercase tracking-widest">
        <ShieldCheck size={13} /> Verified Trust Network
      </div>
      <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-4">
        Every service is <span className="text-emerald-700">expert-backed</span>.
      </h3>
      <p className="text-slate-600 text-sm mt-3 leading-relaxed">
        No middlemen. No fake reviews. Real practising lawyers, chartered accountants, architects and RERA-registered brokers are on the Acre Mitra panel.
      </p>
    </div>

    {/* Live counter cards */}
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-10">
      <CounterCard icon={Scale}     target={5}    suffix="+"   label="Verified Lawyers"       subLabel="Kolkata HC · Calcutta Bar" accent="bg-emerald-50 text-emerald-700" />
      <CounterCard icon={BadgeCheck} target={12}   suffix="+"   label="Property Experts"       subLabel="RERA-registered brokers" accent="bg-amber-50 text-amber-700" />
      <CounterCard icon={Landmark}  target={500}  suffix="+"   label="Successful Deals"       subLabel="Since 2018 across 6 states" accent="bg-violet-50 text-violet-700" />
      <CounterCard icon={Award}     target={200}  suffix="+"    label="Assets Under Advisory" subLabel="Verified transaction value" accent="bg-sky-50 text-sky-700" />
    </div>

    {/* Expert testimonials — floating orbs behind cards */}
    <div className="relative">
      <div className="absolute -top-4 left-1/3 w-72 h-72 rounded-full bg-emerald-200/40 blur-3xl -z-10 animate-pulse" />
      <div className="absolute -bottom-8 right-1/4 w-72 h-72 rounded-full bg-amber-200/40 blur-3xl -z-10 animate-pulse" style={{ animationDelay: '1.2s' }} />
      <div className="grid gap-5 md:grid-cols-3">
        {EXPERTS.map((e, i) => (
          <article
            key={e.name}
            className="relative group rounded-2xl bg-white/80 backdrop-blur border border-slate-200 p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            style={{ animationDelay: `${i * 120}ms` }}
            data-testid={`expert-card-${i}`}
          >
            <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <CheckCircle2 size={10} /> {e.tag}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-emerald-200">
                <img src={e.avatar} alt={e.name} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-600 grid place-items-center ring-2 ring-white">
                  <CheckCircle2 size={10} className="text-white" />
                </span>
              </div>
              <div>
                <div className="text-sm font-black text-slate-900">{e.name}</div>
                <div className="text-[10px] font-semibold text-emerald-700">{e.role}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 mt-4 leading-relaxed italic">"{e.quote}"</p>
            <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
              {e.credential}
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-500 group-hover:w-full transition-all duration-500 rounded-b-2xl" />
          </article>
        ))}
      </div>
    </div>

    {/* Customer reviews — "What our clients say" */}
    <ClientReviews />

    {/* Bottom stripe */}
    <div className="mt-10 rounded-2xl bg-gradient-to-r from-emerald-700 via-emerald-800 to-slate-900 text-white px-6 py-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-300/40 grid place-items-center">
          <Users size={18} className="text-amber-300" />
        </div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-300">Join the panel</div>
          <div className="text-sm font-bold">Are you a practising lawyer, CA, architect or RERA broker?</div>
        </div>
      </div>
      <button
        onClick={() => setJoinOpen(true)}
        className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-xs px-4 py-2 rounded-full transition-colors"
        data-testid="trust-join-panel"
      >
        Apply to be listed <ArrowRight size={12} />
      </button>
    </div>

    {joinOpen && <JoinPanelModal onClose={() => setJoinOpen(false)} />}
  </section>
  );
};

// ---------- Client reviews (5-star, animated on enter) ----------
const StarRating = ({ n = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} width="14" height="14" viewBox="0 0 20 20" className={i < n ? 'fill-amber-400' : 'fill-slate-200'}>
        <path d="M10 1.5l2.72 5.52 6.09.88-4.4 4.29 1.04 6.06L10 15.4l-5.45 2.86 1.04-6.06L1.19 7.9l6.09-.88L10 1.5z" />
      </svg>
    ))}
  </div>
);

const ClientReviews = () => (
  <div className="mt-16" data-testid="client-reviews-section">
    <div className="text-center max-w-2xl mx-auto mb-8">
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-black uppercase tracking-widest">
        <MessageCircle size={13} /> What Our Clients Say
      </div>
      <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-4">
        Real families, <span className="text-amber-600">real reviews</span>.
      </h3>
      <p className="text-slate-600 text-sm mt-3 leading-relaxed">
        500+ families across India and NRI investors have trusted Acre Mitra with their land decisions. Here&rsquo;s what a handful of them told us.
      </p>
    </div>
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
      {REVIEWS.map((r, i) => (
        <article
          key={i}
          className="relative group rounded-2xl bg-gradient-to-br from-white to-amber-50/40 border border-slate-200 p-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          style={{ animationDelay: `${i * 100}ms` }}
          data-testid={`review-card-${i}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="relative w-11 h-11 rounded-full overflow-hidden ring-2 ring-amber-200">
              <img src={r.avatar} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
              {r.verified && (
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-600 grid place-items-center ring-2 ring-white">
                  <CheckCircle2 size={9} className="text-white" />
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-slate-900 truncate">{r.name}</div>
              <div className="text-[10px] font-semibold text-slate-500">{r.location} · {r.when}</div>
            </div>
          </div>
          <StarRating n={r.stars} />
          <p className="text-[13px] text-slate-700 mt-3 leading-relaxed">"{r.text}"</p>
          <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500 group-hover:w-full transition-all duration-500 rounded-b-2xl" />
        </article>
      ))}
    </div>
  </div>
);

// ---------- Join Panel form modal ----------
const JoinPanelModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', role: 'Lawyer', phone: '', email: '', city: '', credential_no: '', years_experience: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.phone || !form.email) {
      setError('Name, phone and email are required.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/panel-applications`, {
        ...form,
        years_experience: parseInt(form.years_experience, 10) || 0,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="join-panel-modal">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white px-6 pt-6 pb-7">
          <div className="flex justify-between items-start">
            <div className="text-[10px] tracking-[3px] uppercase text-amber-300 font-bold">Acre Mitra &middot; Expert Panel</div>
            <button onClick={onClose} className="text-white/70 hover:text-white" aria-label="Close" data-testid="join-panel-close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <h2 className="text-2xl font-bold mt-3">Apply to be a verified expert</h2>
          <p className="text-emerald-100 text-sm mt-1">Get listed on the Acre Mitra trust panel and receive matched client referrals.</p>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
            <h3 className="text-xl font-black text-slate-900">Application received</h3>
            <p className="text-sm text-slate-600 mt-2">Thanks &mdash; our panel team will review your credentials and get in touch within 2 business days.</p>
            <button onClick={onClose} className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-6 py-2.5 rounded-full text-sm" data-testid="join-panel-close-final">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 grid gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Full name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Adv. Anirban Chatterjee" required data-testid="join-panel-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Role *</Label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="join-panel-role">
                  <option>Lawyer</option>
                  <option>Chartered Accountant</option>
                  <option>Architect</option>
                  <option>RERA Broker</option>
                  <option>Surveyor</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Years of experience</Label>
                <Input type="number" min="0" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} placeholder="7" data-testid="join-panel-years" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Phone *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9830011223" required data-testid="join-panel-phone" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@firm.com" required data-testid="join-panel-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Kolkata" data-testid="join-panel-city" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Credential no.</Label>
                <Input value={form.credential_no} onChange={(e) => setForm({ ...form, credential_no: e.target.value })} placeholder="BCI / ICAI / COA / RERA #" data-testid="join-panel-credential" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Anything else?</Label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows="3" className="w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="Tell us about your practice, specialisations, or the areas you serve." data-testid="join-panel-message" />
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="join-panel-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold h-11" data-testid="join-panel-submit">
              {busy ? 'Sending\u2026' : 'Send Application'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
